import { clamp01 } from '../math';
import type { VesselDrawState } from './types';

type Point = readonly [number, number];

/**
 * Sailboat: a low-freeboard sloop with a raked bow (right) and counter stern.
 *
 * The sails are the only self-animating part. The camber breathes and the leech
 * flutters, both growing with the wind, and the masthead fly droops in light air
 * and streams flat in a blow.
 */
export const drawSailVessel = ({
  palette,
  night,
  windMps,
  reducedMotion,
  timeS,
}: VesselDrawState): void => {
  const { ctx } = palette;
  const c = (hex: string): string => palette.colour(hex);

  // hull
  ctx.beginPath();
  ctx.moveTo(-46, -5);
  ctx.lineTo(-30, -7);
  ctx.quadraticCurveTo(8, -7.5, 42, -7); // level deck line (bow same height as stern)
  ctx.quadraticCurveTo(52, -6.5, 50, 0);
  ctx.quadraticCurveTo(38, 4, 8, 6);
  ctx.quadraticCurveTo(-22, 8, -40, 5);
  ctx.quadraticCurveTo(-48, 3, -46, -5);
  ctx.closePath();
  const hull = ctx.createLinearGradient(0, -9, 0, 8);
  hull.addColorStop(0, c('F4F8FB'));
  hull.addColorStop(0.55, c('DCE4EA'));
  hull.addColorStop(1, c('A7B4BE'));
  ctx.fillStyle = hull;
  ctx.fill();
  ctx.lineWidth = 1.3;
  ctx.strokeStyle = c('7C8A96');
  ctx.stroke();

  // boot stripe near the waterline
  ctx.beginPath();
  ctx.moveTo(48, 1);
  ctx.quadraticCurveTo(6, 4, -42, 2);
  ctx.strokeStyle = c('44505A');
  ctx.lineWidth = 1.4;
  ctx.stroke();

  // gunwale highlight
  ctx.beginPath();
  ctx.moveTo(-46, -5);
  ctx.lineTo(-30, -7);
  ctx.quadraticCurveTo(8, -7.5, 42, -7);
  ctx.strokeStyle = c('FFFFFF');
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // low coachroof
  ctx.beginPath();
  ctx.moveTo(-6, -7);
  ctx.lineTo(20, -7);
  ctx.lineTo(17, -12);
  ctx.quadraticCurveTo(6, -13, -3, -12);
  ctx.closePath();
  ctx.fillStyle = c('CDD6DD');
  ctx.fill();
  ctx.strokeStyle = c('8A98A2');
  ctx.lineWidth = 0.7;
  ctx.stroke();

  // standing rigging (forestay + backstay)
  ctx.strokeStyle = c('6E7B86');
  ctx.lineWidth = 0.6;
  ctx.globalAlpha = 0.6;
  ctx.beginPath();
  ctx.moveTo(6, -54);
  ctx.lineTo(46, -6);
  ctx.moveTo(6, -54);
  ctx.lineTo(-45, -5);
  ctx.stroke();
  ctx.globalAlpha = 1;

  const t = reducedMotion ? 0 : timeS;
  const wind = clamp01((windMps - 1) / 11); // 0 calm .. 1 at ~12 m/s

  /**
   * One sail, drawn from tack → head → animated leech back to the clew. The
   * leech bellies out to leeward by the camber, with a travelling ripple on top
   * that grows toward the clew.
   */
  const sail = (
    tack: Point,
    head: Point,
    clew: Point,
    fill: string,
    edge: string,
  ): void => {
    const dx = clew[0] - head[0];
    const dy = clew[1] - head[1];
    const length = Math.hypot(dx, dy) || 1;

    let nx = -dy / length;
    let ny = dx / length;

    // point the leech normal to leeward
    if (
      nx * ((head[0] + clew[0]) / 2 - tack[0]) +
        ny * ((head[1] + clew[1]) / 2 - tack[1]) <
      0
    ) {
      nx = -nx;
      ny = -ny;
    }

    const camber = (2.4 + 4.0 * wind) * (1 + 0.16 * Math.sin(t * 0.9));

    ctx.beginPath();
    ctx.moveTo(tack[0], tack[1]);
    ctx.lineTo(head[0], head[1]);

    for (let i = 0; i <= 12; i += 1) {
      const f = i / 12;
      const bell = Math.sin(f * Math.PI) * camber; // camber, max mid-leech
      const flutter = reducedMotion
        ? 0
        : (0.35 + 1.4 * wind) *
          Math.sin(f * 6.5 - t * (5 + 7 * wind)) *
          f ** 1.5;

      ctx.lineTo(
        head[0] + dx * f + nx * (bell + flutter),
        head[1] + dy * f + ny * (bell + flutter),
      );
    }

    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = edge;
    ctx.lineWidth = 0.6;
    ctx.stroke();
  };

  sail([45, -6], [10, -48], [27, -12], c('ECEAE1'), c('C7C9C0')); // jib (headsail)
  sail([5, -15], [5, -52], [-30, -16], c('F3F1EA'), c('C7C9C0')); // mainsail

  // faint sail panels on the main so it reads as cloth
  ctx.strokeStyle = c('D8D9CF');
  ctx.lineWidth = 0.4;
  ctx.globalAlpha = 0.55;
  [-43, -32, -21].forEach((y) => {
    ctx.beginPath();
    ctx.moveTo(5, y);
    ctx.quadraticCurveTo(-9, y - 1, -21, y + 4);
    ctx.stroke();
  });
  ctx.globalAlpha = 1;

  // mast + boom
  ctx.strokeStyle = c('3A434B');
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(5, -9);
  ctx.lineTo(5, -54);
  ctx.stroke();
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(5, -15);
  ctx.lineTo(-30, -16);
  ctx.stroke();

  // masthead fly: streams downwind, drooping in light air and flat in a blow
  const fly = 3 + 4 * wind;
  const droop = (1 - wind) * 2.6;
  ctx.fillStyle = c('FF5C39');
  ctx.beginPath();
  ctx.moveTo(5, -54);
  ctx.lineTo(
    5 - fly,
    -54.5 + droop + (reducedMotion ? 0 : Math.sin(t * 5) * 0.7),
  );
  ctx.lineTo(5 - fly * 0.82, -52.4 + droop);
  ctx.closePath();
  ctx.fill();

  // nav lights (night only): green sidelight, white stern, masthead, cabin glow
  if (night > 0.04) {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const alpha = Math.min(0.95, night * 1.05);
    palette.navGlow(49, -7, 7, '45,210,95', alpha);
    palette.navGlow(-45, -7, 7, '255,250,235', alpha * 0.9);
    palette.navGlow(5, -55, 8, '255,250,235', alpha);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = `rgba(255,196,120,${0.45 * night})`;
    ctx.beginPath();
    ctx.arc(7, -9, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
};
